import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskFormPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { taskId } = useParams();
  const isEditMode = !!taskId;

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to_list: [],
    priority: 'medium',
    start_date: '',
    due_date: '',
    deadline_date: '',
    status: 'not_started'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  }, []);

  const fetchTask = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        withCredentials: true
      });
      const task = response.data;
      setFormData({
        title: task.title,
        description: task.description,
        assigned_to_list: task.assigned_to_list || [task.assigned_to, task.assigned_to_2, task.assigned_to_3].filter(Boolean),
        priority: task.priority,
        start_date: new Date(task.start_date).toISOString().split('T')[0],
        due_date: new Date(task.due_date).toISOString().split('T')[0],
        deadline_date: new Date(task.deadline_date).toISOString().split('T')[0],
        status: task.status
      });
    } catch (error) {
      console.error('Task fetch error:', error);
      alert('Görev yüklenirken hata oluştu');
      navigate('/tasks');
    }
  }, [taskId, navigate]);

  useEffect(() => {
    if (!user?.is_admin && !user?.is_admin_assistant) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    if (isEditMode) {
      fetchTask();
    }
  }, [user, navigate, isEditMode, fetchUsers, fetchTask]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        assigned_to_list: formData.assigned_to_list,
        assigned_to: formData.assigned_to_list[0] || '',
        assigned_to_2: formData.assigned_to_list[1] || '',
        assigned_to_3: formData.assigned_to_list[2] || '',
        start_date: new Date(formData.start_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        deadline_date: new Date(formData.deadline_date).toISOString()
      };

      if (isEditMode) {
        // Only admin can edit
        if (!user?.is_admin) {
          alert('Sadece admin görev düzenleyebilir');
          return;
        }
        await axios.put(`${API}/tasks/${taskId}`, payload, {
          withCredentials: true
        });
        alert('✅ Görev güncellendi!');
      } else {
        if (user?.is_admin) {
          // Admin directly creates task
          await axios.post(`${API}/tasks`, payload, {
            withCredentials: true
          });
          alert('✅ Görev oluşturuldu!');
        } else if (user?.is_admin_assistant) {
          // Admin assistant creates pending task
          await axios.post(`${API}/pending-tasks`, payload, {
            withCredentials: true
          });
          alert('✅ Görev onaya gönderildi! Admin onayladıktan sonra sisteme eklenecek.');
        }
      }

      navigate('/tasks');
    } catch (error) {
      console.error('Submit error:', error);
      alert('❌ Hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-gray-300 hover:text-white"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-gray-300 hover:text-white"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/tasks')}
                variant="ghost"
                className="text-gray-300 hover:text-white"
              >
                Görevler
              </Button>
              <Button
                onClick={() => navigate('/admin/users')}
                variant="ghost"
                className="text-gray-300 hover:text-white"
              >
                Kullanıcılar
              </Button>
              <div className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2">
                <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-gray-400">Admin</p>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-red-400 hover:text-red-300"
              >
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">
              {isEditMode ? 'Görevi Düzenle' : 'Yeni Görev Oluştur'}
            </h2>
            <p className="text-gray-400">
              {isEditMode ? 'Görev bilgilerini güncelleyin' : 'Ekip üyelerine görev atayın'}
            </p>
          </div>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Görev Bilgileri</CardTitle>
              <CardDescription className="text-gray-400">
                Görev detaylarını eksiksiz doldurun
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-white">
                    Görev Başlığı *
                  </Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Örn: Yeni özellik geliştir"
                    required
                    data-testid="task-title-input"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-white">
                    Açıklama *
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white min-h-32"
                    placeholder="Görev detaylarını açıklayın..."
                    required
                    data-testid="task-description-input"
                  />
                </div>

                {/* Görev Ataması - Çoktan Seçmeli */}
                <div className="space-y-2">
                  <Label className="text-white">
                    Atanan Kişiler * ({formData.assigned_to_list.length} kişi seçildi)
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700 max-h-48 overflow-y-auto">
                    {users.map((u) => {
                      const isSelected = formData.assigned_to_list.includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            const list = isSelected
                              ? formData.assigned_to_list.filter(id => id !== u.id)
                              : [...formData.assigned_to_list, u.id];
                            setFormData({ ...formData, assigned_to_list: list });
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                            isSelected ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                          data-testid={`assign-user-${u.id}`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-white border-white' : 'border-gray-500'}`}>
                            {isSelected && <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <span className="truncate">{u.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  {formData.assigned_to_list.length === 0 && (
                    <p className="text-xs text-red-400">En az bir kişi seçmelisiniz</p>
                  )}
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label htmlFor="priority" className="text-white">
                    Öncelik *
                  </Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="low">Düşük</SelectItem>
                      <SelectItem value="medium">Orta</SelectItem>
                      <SelectItem value="high">Yüksek</SelectItem>
                      <SelectItem value="urgent">Acil</SelectItem>
                      <SelectItem value="very_urgent">Çok Acil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label htmlFor="start_date" className="text-white">
                    Başlangıç Tarihi *
                  </Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    required
                    data-testid="start-date-input"
                  />
                </div>

                {/* Due Date */}
                <div className="space-y-2">
                  <Label htmlFor="due_date" className="text-white">
                    Son Tarih *
                  </Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    required
                    data-testid="due-date-input"
                  />
                </div>

                {/* Deadline Date */}
                <div className="space-y-2">
                  <Label htmlFor="deadline_date" className="text-white">
                    Termin Tarihi *
                  </Label>
                  <Input
                    id="deadline_date"
                    type="date"
                    value={formData.deadline_date}
                    onChange={(e) => setFormData({ ...formData, deadline_date: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    required
                    data-testid="deadline-date-input"
                  />
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <Label htmlFor="status" className="text-white">
                    Durum *
                  </Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="not_started">Başlanmadı</SelectItem>
                      <SelectItem value="planned">Planlanan</SelectItem>
                      <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                      <SelectItem value="project_work">Proje İşleri</SelectItem>
                      <SelectItem value="tender_phase">İhale Aşamasında</SelectItem>
                      <SelectItem value="completed">Tamamlandı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    disabled={loading}
                    data-testid="submit-task-button"
                  >
                    {loading ? 'Kaydediliyor...' : (isEditMode ? 'Güncelle' : 'Oluştur')}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => navigate('/tasks')}
                    variant="outline"
                    className="flex-1 bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                    data-testid="cancel-button"
                  >
                    İptal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default TaskFormPage;
